import React from 'react';
import { Settings2 } from 'lucide-react';
import { useProjectStore } from '../store/useProjectStore';

/**
 * GlobalFactorySettings
 * --------------------------------------------------------------------
 * Painel isolado e enxuto para a configuração global de fábrica
 * (Turnos / Min por turno / Dias úteis por mês).
 *
 * Antes vivia incrustado no painel de propriedades do card, poluindo
 * o foco em parâmetros individuais. Agora é um componente standalone,
 * com tipografia Title Case, bordas suaves e respiração adequada para
 * uma ferramenta de uso corporativo.
 *
 * NOTA: ainda NÃO é renderizado no RightPanel. Será posicionado na
 * Officebar / barra de status ou em modal dedicado num passo posterior.
 */
export const GlobalFactorySettings: React.FC = () => {
  const factoryShifts = useProjectStore((s) => s.factoryShifts);
  const factoryMinPerShift = useProjectStore((s) => s.factoryMinPerShift);
  const factoryWorkDays = useProjectStore((s) => s.factoryWorkDays);
  const setFactoryShifts = useProjectStore((s) => s.setFactoryShifts);
  const setFactoryMinPerShift = useProjectStore((s) => s.setFactoryMinPerShift);
  const setFactoryWorkDays = useProjectStore((s) => s.setFactoryWorkDays);

  const minPerDay = factoryShifts * factoryMinPerShift;

  const labelClass =
    'text-[11px] font-medium text-slate-500 dark:text-slate-400 mb-1.5 block';
  const inputClass =
    'w-full h-8 px-2 text-sm font-semibold text-slate-900 dark:text-slate-100 ' +
    'bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/60 ' +
    'rounded-md text-center outline-none transition-colors ' +
    'focus:bg-white dark:focus:bg-slate-800 focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20';

  return (
    <section
      aria-label="Configuração global da fábrica"
      className="rounded-lg bg-white dark:bg-slate-900/40 border border-slate-200/70 dark:border-slate-700/50 px-4 py-3"
    >
      <header className="flex items-center gap-2 mb-3">
        <Settings2
          size={14}
          className="text-slate-400 dark:text-slate-500"
          aria-hidden
        />
        <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">
          Configuração Global da Fábrica
        </h3>
      </header>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <label htmlFor="gfs-shifts" className={labelClass}>
            Turnos
          </label>
          <input
            id="gfs-shifts"
            type="number"
            min={1}
            max={3}
            placeholder="2"
            value={factoryShifts}
            onChange={(e) =>
              setFactoryShifts(Math.max(1, parseInt(e.target.value, 10) || 1))
            }
            onFocus={(e) => e.target.select()}
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="gfs-min" className={labelClass}>
            Min por Turno
          </label>
          <input
            id="gfs-min"
            type="number"
            min={1}
            placeholder="480"
            value={factoryMinPerShift}
            onChange={(e) =>
              setFactoryMinPerShift(
                Math.max(1, parseInt(e.target.value, 10) || 480),
              )
            }
            onFocus={(e) => e.target.select()}
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="gfs-days" className={labelClass}>
            Dias por Mês
          </label>
          <input
            id="gfs-days"
            type="number"
            min={1}
            max={31}
            placeholder="20"
            value={factoryWorkDays}
            onChange={(e) =>
              setFactoryWorkDays(
                Math.max(1, parseInt(e.target.value, 10) || 20),
              )
            }
            onFocus={(e) => e.target.select()}
            className={inputClass}
          />
        </div>
      </div>

      <p className="mt-3 text-[11px] text-slate-400 dark:text-slate-500 font-mono">
        {minPerDay.toLocaleString('pt-BR')} min/dia · {factoryWorkDays} dias úteis/mês
      </p>
    </section>
  );
};

export default GlobalFactorySettings;
